import { Alert, Button, Tag } from 'antd';
import type { CaseOverride } from '@/api/caseActions';
import { formatAbsolute } from '@/lib/time';
import { erasedOr } from '@/lib/erased';

/**
 * Overrides waiting for a second reviewer (owner ruling 9). The server refuses the proposer and decides who
 * holds the permission; this only saves the proposer a refused click.
 */
export function OverrideReviewPanel({
  overrides,
  mySub,
  onReview,
}: {
  overrides: CaseOverride[];
  mySub?: string;
  onReview: (o: CaseOverride, answer: 'approve' | 'reject') => void;
}) {
  const waiting = overrides.filter((o) => o.status === 'PROPOSED');
  if (waiting.length === 0) return null;
  return (
    <section aria-label="Overrides awaiting a second reviewer" data-testid="override-review" style={{ marginBottom: 12 }}>
      {waiting.map((o) => {
        const mine = !!mySub && o.proposedBySub === mySub;
        const n = o.evidenceHashes.length;
        return (
          <Alert
            key={o.overrideId}
            type="warning"
            showIcon
            style={{ marginBottom: 8, borderRadius: 8 }}
            data-testid="override-proposal"
            message={
              <span>
                <Tag bordered={false} color={o.finding === 'REJECTED' ? 'error' : 'success'}>
                  {o.finding === 'REJECTED' ? 'Reject' : 'Accept'} {n} evidence record{n === 1 ? '' : 's'}
                </Tag>
                {mine ? 'You proposed this. A second reviewer must decide it.' : `${o.proposedBy} proposed this. It has no effect until a second reviewer approves it.`}
              </span>
            }
            description={`${erasedOr(o.reason)} (${formatAbsolute(o.proposedAt)})`}
            action={
              mine ? undefined : (
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  <Button size="small" type="primary" data-testid="override-approve" onClick={() => onReview(o, 'approve')}>
                    Approve
                  </Button>
                  <Button size="small" danger data-testid="override-reject" onClick={() => onReview(o, 'reject')}>
                    Reject
                  </Button>
                </span>
              )
            }
          />
        );
      })}
    </section>
  );
}
